import { FunctionComponent } from "react";
import { Dialog } from "@headlessui/react";
import { Calendar, X } from "react-feather";
import { ModalContainer } from "components/modal/ModalContainer";
import eventIllustration from "assets/images/template-event-png.png";
import { formatDay, organizationNames, weekdayOf } from "../eventFormat";

interface ComponentProps {
  open: boolean;
  onClose: () => void;
  data: any;
}

/**
 * One event, in full.
 *
 * Four things were wrong with this modal, and only one of them was the MUI.
 *
 * It had **no way to close it**. The edit and cancel buttons at the bottom
 * were commented out, and `ModalContainer` supplies no chrome of its own, so
 * the only exits were the Escape key and a click on the backdrop -- neither of
 * which a phone offers or advertises. There is a close button now.
 *
 * It was **663px wide with a 394px minimum height**, both fixed. On a phone
 * the flexbox around it squeezed the card and the content inside it; on a
 * desktop a two-line event left roughly two hundred pixels of empty gold below
 * the text, because the height was reserved for a decorative band the content
 * had to clear. The card is the size of what is in it.
 *
 * It printed **"Invalid date"** for any event with no lunar date, from an
 * unguarded `moment(undefined).format()`; and it laid the chi and the phái out
 * with `justify-content: space-between` and `pr: 16`, so a single
 * organisation sat alone at the left with 128px of nothing beside it.
 */
export const ModalEventDetail: FunctionComponent<ComponentProps> = ({
  open,
  onClose,
  data,
}) => {
  const lunar = data?.fromLunarDate ? `(${formatDay(data.fromLunarDate)} âm lịch)` : "";
  const weekday = data?.fromDate ? weekdayOf(data.fromDate) : "";
  const orgs = organizationNames(data?.organizations);
  const images: any[] = Array.isArray(data?.images) ? data.images : [];

  return (
    <ModalContainer
      isOpen={open}
      className="w-full max-w-[663px]"
      closeModal={onClose}
    >
      <div className="relative bg-gold-soft text-brand">
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-brand
           hover:bg-white/60 transition-colors
           focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <X size={20} />
        </button>

        <div className="max-h-[85vh] overflow-y-auto px-5 py-5 sm:px-8 sm:py-7">
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              {/* Headless UI's Dialog reads its accessible name from this, so
                  the modal announces the event rather than nothing at all. */}
              <Dialog.Title className="pr-8 text-h3 font-bold">{data?.name}</Dialog.Title>

              <div className="mt-3 flex items-start gap-2 text-sm">
                <Calendar aria-hidden size={16} className="mt-1 shrink-0" />
                <span>
                  {[[weekday, formatDay(data?.fromDate)].filter(Boolean).join(", "), lunar]
                    .filter(Boolean)
                    .join(" ")}
                </span>
              </div>

              {orgs.length > 0 && (
                <div className="mt-1 text-sm text-brand/80">{orgs.join(" · ")}</div>
              )}
            </div>

            {/* Decorative. Hidden on a phone, where it was taking a third of
                the width away from the text it illustrates. */}
            <img
              src={eventIllustration}
              alt=""
              aria-hidden
              className="hidden sm:block w-[112px] shrink-0"
            />
          </div>

          {data?.history && (
            <div
              className="mt-5 whitespace-pre-line text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: data.history }}
            />
          )}

          {images.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-3">
              {images.map((item: any) => (
                <img
                  key={item.id}
                  src={item.imageUrl}
                  alt=""
                  className="h-[90px] w-[100px] rounded-lg object-cover"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalContainer>
  );
};
